import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Table,
  Dropdown,
  Menu,
  Space,
  Button,
  Modal,
  Popover,
} from "antd";
import { ColumnsType, ColumnGroupType, ColumnType } from "antd/es/table";
import styles from "./DashBoard.module.scss";
import getTripIcon from "../../helpers/getTripIcon";
import moment from "moment";
import {
  columnSortIcon,
  scheduleServiceRightArrow,
  expandRowDownArrowIcon,
  expandRowRightArrowIcon,
  favoriteHeartIcon,
  rebookDesktopIcon,
  cancelServiceDesktopIcon,
  greyCIcon,
  infoIcon,
  brokenHeartIcon,
} from "../../assets/images";
import { useTripsCtx } from "../../context/TripDetailsContext";
import { RebookTripOptions, useTripActions } from "../../hooks/useTripActions";
import {
  IFavRecords,
  ITripDetails,
} from "../ServiceUsers/Interfaces/ITripDetails";
import { useWindowSize } from "../../hooks/useWindowSize";
import AddressTimeline from "../Shared/AddressTimeline";
import GMapsModal from "../Shared/GMapsModal";
import TripTimings from "../Shared/TripTimings";
import CustomButton from "../../common/Buttons/CustomButton";
import { useHistory, useLocation } from "react-router-dom";
import CustomTooltip from "../../common/Tooltips/CustomTooltip";
import DashboardFavoriteModal from "./DashboardFavoriteModal";
import GoogleLoader from "../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { usePaginationCtx } from "../../context/PaginationContext";
import RemoveFavoriteModal from "../Shared/RemoveFavoriteModal";
import { defaultTripDetails } from "../ServiceUsers/ServiceUsersMobile/defaultTripDetails";
import { notification } from "antd";
import { TrackAnalytics } from "../../types/ApplicationTypes";
import {
  SUProfileRebookMessage,
  SourcePageTypes,
  dashboardRebookMessage,
} from "../../types/TripTypes";

interface TripsColumnType {
  key: React.Key;
  tripDate: string;
  appointmentTime: string;
  serviceUser: string;
  tripCost: number;
  tripReason: string;
  favoriteName?: string;
}

type Props = {
  dataSource: any;
  isCalling: string;
  onScheduleService?: () => void;
  paginationConfig?: any;
  hideNoServiceMessage?: boolean;
  hideDateSort?: boolean;
  isBookingIdHyperlink?: boolean;
};

const DashBoardTable: React.FC<Props> = ({
  dataSource,
  isCalling,
  onScheduleService,
  paginationConfig,
  hideNoServiceMessage,
  hideDateSort,
  isBookingIdHyperlink,
}) => {
  const {
    isLoading,
    upcomingTripDetailsCtx,
    pastTripDetailsCtx,
    favoriteTripDetailsCtx,
  } = useTripsCtx();
  const [upcomingTripDetails, setUpcomingTripDetails] = upcomingTripDetailsCtx;
  const [pastTripDetails] = pastTripDetailsCtx;
  const [favoriteTripDetails] = favoriteTripDetailsCtx;

  const { activeTabCtx, resetPagination } = usePaginationCtx();
  const [activeTab] = activeTabCtx;

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isFavModalVisible, setIsFavModalVisible] = useState<boolean>(false);
  const [cancelTripRecord, setCancelTripRecord] = useState<ITripDetails>(
    defaultTripDetails
  );
  const [favRecords, setFavRecords] = useState<IFavRecords>();
  const [favoriteTripsToRemove, setFavoriteTripsToRemove] = useState<
    ITripDetails[]
  >([]);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState<boolean>(
    false
  );
  const [visiblePopover, setVisiblePopover] = useState<number | null>(null);

  const handleVisibleChange = (id: number, visible: boolean) => {
    setVisiblePopover(visible ? id : null);
  };

  const { width } = useWindowSize();

  const { rebookTrip, cancelTrip, removeFavoriteTrip } = useTripActions();
  const { resetFavoritePagination, sortOrderCtx } = usePaginationCtx();
  const [sourcePage, setSourcePage] = useState<string>(
    SourcePageTypes.Dashboard
  );

  const [, setSortOrder] = sortOrderCtx;
  const history = useHistory();
  const { pathname } = useLocation();

  const handleBookingId = (record: ITripDetails) => {
    history.push(
      `/DashBoard/TransportationServiceSetupSummary/${record.personId}/${record.requestAssignmentId}/${record.bookingId}/${record.tripStatusId}/${record.tripDate}`
    );
  };

  useEffect(() => {
    width <= 992 && isModalVisible && setIsModalVisible(false);
  }, [width]);

  const handleFavorite = async (record: ITripDetails) => {
    if (activeTab === "2") {
      try {
        const totalTrips = [...upcomingTripDetails, ...pastTripDetails];
        const allTripLegs = totalTrips.filter(
          (trip: ITripDetails) =>
            trip.requestAssignmentId === record.requestAssignmentId
        );
        const data = {
          selectedLeg: [record],
          allLegs: allTripLegs,
          legsCount: allTripLegs.length,
        };
        setFavRecords(data);
        setIsFavModalVisible(true);
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to add trip as favorite",
        });
      }
    }
  };

  const shouldHideColumn = (
    column: ColumnGroupType<TripsColumnType> | ColumnType<TripsColumnType>
  ): boolean => {
    if (
      ["ServiceUserList", "TabsComponent"].includes(isCalling) &&
      column.key === "serviceUser"
    ) {
      return false;
    }
    if (
      ["YourPlan", "BillingHistory"].includes(isCalling) &&
      column.key === "actions"
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    const isServiceUserProfile = pathname
      .split("/")
      .some((pathSegment) =>
        ["ManageServiceUsers", "ManageServiceUsersMobile"].includes(pathSegment)
      );
    setSourcePage(
      isServiceUserProfile
        ? SourcePageTypes.SUProfile
        : SourcePageTypes.Dashboard
    );
  }, []);

  const handleRebook = (trip: RebookTripOptions) => {
    if (sourcePage === SourcePageTypes.Dashboard) {
      const event = dashboardRebookMessage[activeTab];
      TrackAnalytics("/DashBoard", event, "DashBoard Page");
    } else {
      const event = SUProfileRebookMessage[activeTab];
      TrackAnalytics(
        "/DashBoard/ManageServiceUsers",
        event,
        "ManageServiceUsers Page"
      );
    }
    rebookTrip(trip);
  };

  const handleFavoritesDetails = (trip: ITripDetails) =>
    history.push(
      `/DashBoard/FavoriteDetails/${trip.personId}/${trip.requestAssignmentId}/${trip.bookingId}/${trip.favoriteId}/${trip.tripDate}?source=${sourcePage}`
    );

  const handleRemoveFavorite = (selectedTrip: ITripDetails) => {
    const tripLegsToRemove = favoriteTripDetails.filter(
      (trip: ITripDetails) => trip.id === selectedTrip.id
    );
    setFavoriteTripsToRemove(tripLegsToRemove);
    setVisiblePopover(null);
    setIsRemoveModalVisible(true);
  };

  const handleSortToggle = () => {
    setSortOrder((prevOrder: { [tab: string]: string }) => ({
      ...prevOrder,
      [activeTab]: prevOrder[activeTab] === "DESC" ? "ASC" : "DESC",
    }));
  };

  const columnsData: ColumnsType<TripsColumnType> = [
    {
      title: hideDateSort ? (
        <Row>Date</Row>
      ) : (
        <Row
          gutter={[24, 0]}
          className={styles.sortColumnHeading}
          onClick={handleSortToggle}
        >
          <Col>Date</Col>
          <Col>
            <img src={columnSortIcon} alt="date sorting filter" />
          </Col>
        </Row>
      ),

      key: "tripDate",
      width: 100,
      render: (record): JSX.Element => (
        <span>{moment(record.tripDate).format("MM/DD/YY")}</span>
      ),
    },
    {
      title: "Time",
      key: "appointmentTime",
      width: 100,
      ellipsis: true,
      render: (record): JSX.Element => (
        <span>
          {record.isWillCall
            ? "Call when ready"
            : moment(record.appointmentTime || record.requestPickTime).format(
                "hh:mm A"
              )}
        </span>
      ),
    },
    {
      title: "Service User",
      dataIndex: "serviceUser",
      key: "serviceUser",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Cost",
      key: "tripCost",
      render: (record): JSX.Element => (
        <span>
          ${record?.tripCost?.toFixed(2)}{" "}
          {record.statusDetail === "Cancelled" && (
            <CustomTooltip
              tooltipText="Canceled"
              className="customTooltip"
              icon={greyCIcon}
            />
          )}
        </span>
      ),
      width: 120,
    },
    {
      title: "Type",
      key: "tripReason",
      width: 100,
      render: (record): JSX.Element => (
        <Space>
          {getTripIcon(record.tripReason)}
          Ride
        </Space>
      ),
    },
    {
      title: "",
      width: 80,
      key: "actions",
      render: (record) => (
        <Dropdown
          overlay={
            <Menu
              id={styles.customDropdown}
              className={styles.optionsDropdownSize}
            >
              {activeTab === "2" && (
                <Menu.Item
                  key="0"
                  className={styles.actionsItem}
                  onClick={() => handleFavorite(record)}
                >
                  <img src={favoriteHeartIcon} />
                  Add to Favorites
                </Menu.Item>
              )}
              <Menu.Item
                key="1"
                className={activeTab === "2" ? "" : styles.actionsItem}
                onClick={() => handleRebook({ selectedLeg: record })}
              >
                <img src={rebookDesktopIcon} />
                Rebook Service
              </Menu.Item>
              {activeTab === "1" && (
                <Menu.Item key="2" onClick={() => handleCancelService(record)}>
                  <img src={cancelServiceDesktopIcon} />
                  Cancel Service
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
          placement="bottomRight"
        >
          <div className={styles.noRipple}>
            <Button type="default" className={styles.actionsBtn}>
              Actions
            </Button>
          </div>
        </Dropdown>
      ),
    },
  ];

  const favoritedColumnsData = [
    {
      title: (
        <Row
          justify="space-between"
          className={styles.sortColumnHeading}
          onClick={handleSortToggle}
        >
          <Col>Name</Col>
          <Col>
            <img src={columnSortIcon} alt="sort" />
          </Col>
        </Row>
      ),
      dataIndex: "favoriteName",
      key: "favoriteName",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Time",
      key: "appointmentTime",
      width: 150,
      render: (record: ITripDetails): JSX.Element => (
        <Row justify="start" align="middle" gutter={[3, 0]}>
          <Col>
            {record.isWillCall
              ? "Call when ready"
              : moment(record.appointmentTime || record.requestPickTime).format(
                  "hh:mm A"
                )}
          </Col>
          <Col>
            <CustomTooltip
              tooltipText={`You'll be able to edit the details of
                            this favorite after clicking the 
                            "Rebook" button.`}
              className="customTooltip"
              icon={infoIcon}
            />
          </Col>
        </Row>
      ),
    },
    {
      title: "Service User",
      dataIndex: "serviceUser",
      key: "serviceUser",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Type",
      key: "tripReason",
      width: 70,
      render: (record: ITripDetails): JSX.Element => (
        <Space>
          {getTripIcon(record.tripReason)}
          Ride
        </Space>
      ),
    },
    {
      title: "",
      width: 220,
      key: "actions",
      render: (record: ITripDetails) => (
        <Row justify="center" gutter={[5, 0]}>
          <Col>
            <CustomButton
              text="Rebook"
              type="default"
              className="tableBtns-rebook"
              handlerFunction={() =>
                handleRebook({ selectedLeg: record, areTripLegsMissing: true })
              }
            />
          </Col>
          <Col>
            <CustomButton
              text="Details"
              type="default"
              className="tableBtns"
              handlerFunction={() => handleFavoritesDetails(record)}
            />
          </Col>
          <Col>
            <Popover
              overlayClassName={styles.favActionBtnPopover}
              content={
                <CustomButton
                  icon={brokenHeartIcon}
                  type="default"
                  text="Remove Favorite"
                  id="popoverBtn"
                  handlerFunction={() => handleRemoveFavorite(record)}
                />
              }
              trigger="click"
              visible={visiblePopover === record.id}
              onVisibleChange={(visible) =>
                handleVisibleChange(record.id, visible)
              }
              placement="bottomLeft"
            >
              <div className={styles.noRipple}>
                <Button type="default" className={styles.actionsBtn}>
                  Actions
                </Button>
              </div>
            </Popover>
          </Col>
        </Row>
      ),
    },
  ];

  function customExpandIcon(props: {
    expanded: any;
    onExpand: (
      arg0: any,
      arg1: React.MouseEvent<HTMLImageElement, MouseEvent>
    ) => void;
    record: any;
  }) {
    return (
      <img
        onClick={(e) => {
          props.onExpand(props.record, e);
        }}
        src={props.expanded ? expandRowDownArrowIcon : expandRowRightArrowIcon}
        alt="expand row"
      />
    );
  }

  const handleCancelService = (record: ITripDetails) => {
    setIsModalVisible(true);
    setCancelTripRecord(record);
  };

  const handleClose = (): void => setIsModalVisible(false);

  const handleYesRemove = () => {
    removeFavoriteTrip(favoriteTripsToRemove);
    setIsRemoveModalVisible(false);
    resetFavoritePagination();
  };

  const handleYesCancel = () => {
    cancelTrip(cancelTripRecord);
    TrackAnalytics("/DashBoard", "Trip is cancelled", "Dashboard Page");
    const filterCanceledTrips = upcomingTripDetails.filter(
      (upcomingTrip: ITripDetails) =>
        upcomingTrip.requestAssignmentId !==
        cancelTripRecord.requestAssignmentId
    );
    setUpcomingTripDetails(filterCanceledTrips);
    setIsModalVisible(false);
  };

  let expandableProps;

  if (activeTab !== "3") {
    expandableProps = {
      expandIcon: ({ expanded, onExpand, record }: any) =>
        customExpandIcon({ expanded, onExpand, record }),
      expandedRowRender: (record: any) => {
        return (
          <>
            <Row className={styles.expandRowMargin}>
              <Col span={12}>
                <TripTimings trip={record} />
                <Row className={styles.expandHeadingMargin}>
                  <Col span={24} className={styles.expandHeading}>
                    Trip ID
                  </Col>
                  <Col span={24} className={styles.expandDetails}>
                    {record?.tripId}
                  </Col>
                </Row>
                <Row className={styles.expandHeadingMargin}>
                  <Col span={24} className={styles.expandHeading}>
                    Booking ID
                  </Col>
                  <Col span={24}>
                    {isBookingIdHyperlink ? (
                      <CustomButton
                        text={record?.bookingId}
                        type="link"
                        handlerFunction={() => handleBookingId(record)}
                        className="hyperLinkBtn"
                      />
                    ) : (
                      record?.bookingId
                    )}
                  </Col>
                </Row>
              </Col>
              <Col
                span={12}
                className={styles.addressBorder}
                id={styles.addressColumn}
              >
                <AddressTimeline trip={record} />
                <GMapsModal
                  trip={record}
                  layoutWidth="281px"
                  layoutHeight="167px"
                  modalWidth="807px"
                  modalHeight="501px"
                />
              </Col>
            </Row>
          </>
        );
      },
    };
  }

  const displayNoServiceMessage = () => {
    switch (activeTab) {
      case "1":
        return (
          upcomingTripDetails.length === 0 && (
            <Row justify="center">
              <Col>
                <Row>
                  <Col className={styles.noServicesText}>
                    There are no upcoming service requests.
                  </Col>
                </Row>
                <Row justify="center">
                  <Col className={styles.noRipple}>
                    <Button
                      className={styles.tableAddServiceRequestBtn}
                      onClick={onScheduleService}
                    >
                      Schedule a Service
                      <img
                        src={scheduleServiceRightArrow}
                        className={styles.scheduleServiceImage}
                      />
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          )
        );
      case "2":
        return (
          pastTripDetails?.length === 0 && (
            <Row justify="center">
              <Col className={styles.noFavoritesText}>No services to show.</Col>
            </Row>
          )
        );
      case "3":
        return (
          favoriteTripDetails.length === 0 && (
            <Row justify="center">
              <Col className={styles.noFavoritesText}>
                You have no favorited services.
              </Col>
            </Row>
          )
        );
    }
  };

  return isLoading ? (
    <GoogleLoader />
  ) : (
    <div
      id={styles.tableWrapper}
      className={
        ["TabsComponent"].includes(isCalling)
          ? styles.serviceUserProfileTableHead
          : styles.tableHead
      }
    >
      <Table
        className={styles.upcomingTable}
        rowKey={activeTab !== "3" ? "bookingId" : "id"}
        pagination={paginationConfig || false}
        scroll={{ y: 480 }}
        columns={
          activeTab === "3"
            ? favoritedColumnsData.filter(shouldHideColumn)
            : columnsData.filter(shouldHideColumn)
        }
        dataSource={dataSource}
        expandable={expandableProps}
      />
      {!hideNoServiceMessage && displayNoServiceMessage()}
      <Modal
        visible={isModalVisible}
        className={styles.modalContainer}
        footer={
          <Row justify="center" gutter={[10, 0]} className={styles.modalBtns}>
            <Col>
              <Button className={styles.modalCloseBtn} onClick={handleClose}>
                Close
              </Button>
            </Col>
            <Col>
              <Button className={styles.modalYesBtn} onClick={handleYesCancel}>
                Yes, Cancel
              </Button>
            </Col>
          </Row>
        }
      >
        <Row justify="center">
          <Col className={styles.modalHeading}>Cancel Service</Col>
        </Row>
        <Row justify="center" className={styles.modalContent}>
          <Col>
            Are you sure you want to cancel this service?
            <br /> This cannot be undone.
          </Col>
        </Row>
      </Modal>
      <DashboardFavoriteModal
        favModalVisible={isFavModalVisible}
        favRecords={favRecords}
        setFavModalVisible={setIsFavModalVisible}
        isDesktop
      />
      <RemoveFavoriteModal
        isRemoveModalVisible={isRemoveModalVisible}
        setIsRemoveModalVisible={setIsRemoveModalVisible}
        handleYesRemove={handleYesRemove}
      />
    </div>
  );
};

export default React.memo(DashBoardTable);
